import el from "@cypherpotato/el";

const compactTokenFormatter = new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 1
});

const integerTokenFormatter = new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 0
});

function formatCompactTokens(tokens) {
    const value = Math.max(0, Math.round(tokens || 0));

    if (value < 1000)
        return `${integerTokenFormatter.format(value)} tokens`;

    if (value < 1000000)
        return `${compactTokenFormatter.format(value / 1000)}K tokens`;

    if (value < 1000000000)
        return `${compactTokenFormatter.format(value / 1000000)}M tokens`;

    return `${compactTokenFormatter.format(value / 1000000000)}B tokens`;
}

function dailyUsageTokens(item) {
    return (Number(item.tokenUsageMillions) || 0) * 1000000;
}

function percentile(values, percentileValue) {
    if (values.length === 0)
        return 0;

    const index = (values.length - 1) * percentileValue;
    const lowerIndex = Math.floor(index);
    const upperIndex = Math.ceil(index);

    if (lowerIndex === upperIndex)
        return values[lowerIndex];

    return values[lowerIndex] + (values[upperIndex] - values[lowerIndex]) * (index - lowerIndex);
}

export function DailyUsageWidget(dailyTokenUsage = []) {
    const hoveringItem = el.state.create(null);
    const currentYear = new Date().getFullYear();
    const yearStart = new Date(currentYear, 0, 1);
    const yearEnd = new Date(currentYear, 11, 31);
    const dayMs = 24 * 60 * 60 * 1000;
    const dayCount = Math.round((yearEnd - yearStart) / dayMs) + 1;
    const weekCount = Math.ceil(dayCount / 7);
    const angleStep = 360 / weekCount;
    const usageByDay = new Map(dailyTokenUsage.map(item => [String(item.day).slice(0, 10), dailyUsageTokens(item)]));
    const totalTokens = dailyTokenUsage.reduce((sum, item) => sum + dailyUsageTokens(item), 0);
    const positiveTokenValues = dailyTokenUsage
        .map(dailyUsageTokens)
        .filter(tokens => tokens > 0)
        .sort((a, b) => a - b);
    const p05 = percentile(positiveTokenValues, .05);
    const p95 = percentile(positiveTokenValues, .95);
    const scoreRange = Math.log(p95 + 1) - Math.log(p05 + 1);
    const center = 50;
    const outerRadius = 39;
    const ringGap = 3.55;
    const tileHeight = 1.75;
    const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
    const dayFormatter = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" });

    const dayCells = Array.from({ length: dayCount }, (_, index) => {
        const day = new Date(currentYear, 0, index + 1);
        const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
        const weekIndex = Math.floor(index / 7);
        const ringIndex = index % 7;
        const angle = -90 + weekIndex * angleStep;
        const radians = angle * Math.PI / 180;
        const radius = outerRadius - ringIndex * ringGap;
        const tileWidth = Math.max(1.35, (2 * Math.PI * radius / weekCount) * .72);
        const tokens = usageByDay.get(dayKey) || 0;
        const score = tokens <= 0 || scoreRange <= 0
            ? 0
            : Math.min(1, Math.max(0, (Math.log(tokens + 1) - Math.log(p05 + 1)) / scoreRange));
        const level = tokens <= 0
            ? 0
            : Math.min(5, Math.max(1, Math.ceil(score * 5)));
        const tokensLabel = formatCompactTokens(tokens);
        const primaryMix = Math.round(20 + score * 76);
        const borderMix = Math.round(34 + score * 66);

        return el("span.daily-usage-cell", {
            classList: [`level-${level}`],
            title: tokensLabel,
            "aria-label": `${tokensLabel} on ${day.toLocaleDateString()}`,
            style: {
                "--x": `${center + Math.cos(radians) * radius}%`,
                "--y": `${center + Math.sin(radians) * radius}%`,
                "--rotate": `${angle + 90}deg`,
                "--tile-width": `${tileWidth}%`,
                "--tile-height": `${tileHeight}%`,
                "--usage-fill": `color-mix(in srgb, var(--primary-color) ${primaryMix}%, var(--bg-200))`,
                "--usage-border": `color-mix(in srgb, var(--primary-color) ${borderMix}%, var(--border-color))`
            },
            onMouseOver() {
                hoveringItem.value = { day, tokens };
            },
            onMouseLeave() {
                hoveringItem.value = null;
            }
        });
    });

    const monthLabels = Array.from({ length: 12 }, (_, month) => {
        const monthDate = new Date(currentYear, month, 1);
        const dayIndex = Math.round((monthDate - yearStart) / dayMs);
        const weekIndex = Math.floor(dayIndex / 7);
        const angle = -90 + weekIndex * angleStep;
        const radians = angle * Math.PI / 180;

        return el("span.daily-usage-month", {
            style: {
                "--x": `${center + Math.cos(radians) * 46}%`,
                "--y": `${center + Math.sin(radians) * 46}%`
            }
        }, monthFormatter.format(monthDate));
    });

    return el(".daily-usage-widget",
        el(".daily-usage-header",
            el("div",
                el("span", "Daily token usage"),
                el("strong", el.state.listening([hoveringItem], () =>
                    hoveringItem.value && el(".daily-usage-token-state",
                        el(".it_day-item", dayFormatter.format(hoveringItem.value.day), "//"),
                        el(".it-usage-item", formatCompactTokens(hoveringItem.value.tokens))
                    ) || el(".daily-usage-token-main",
                        formatCompactTokens(totalTokens)
                    )
                ))
            ),
            el("span", currentYear)
        ),
        el(".daily-usage-map",
            {
                role: "img",
                "aria-label": `Daily token usage in ${currentYear}`
            },
            ...dayCells,
            ...monthLabels
        ),
        el(".daily-usage-legend",
            el("span", "Less"),
            ...[0, 1, 2, 3, 4, 5].map(level =>
                el("i", { classList: [`level-${level}`] })
            ),
            el("span", "More")
        )
    );
}
